import {ElementMeasure, getElementMeasure} from "@fd/helpers/DomUtils.ts";

export const DRAG_TYPE = "application/crm";
export const PLAIN_TYPE = "text/plain";

export type DragTypeParams = { [key:string]:string|number };

export interface ParsedType {
	type: string;
	full: string;
	dragType?: boolean;
	params?: DragTypeParams;
}

/**
 * CRM uses this object format to reference any draggable record.
 * Please note that id is optional. That's because a new object
 * can be dragged to and is technically created on drop.
 */
export interface ObjectRef {
	/** This mandatory tag is here to allow receiver to test identity of JSON object */
	_is_ref: true;

	type: number;
	id?: number|string;
	subType?: number;
	meta?: DragTypeParams;
}

export function refToUID(or:ObjectRef):string {
	const uid: string[] = [];
	uid.push(or.type.toString());
	uid.push(or.subType ? or.subType.toString() : "");
	uid.push(or.id ? or.id.toString() : "");
	return uid.join('@');
}

export function isValidInteger(str:string):boolean {
	return /^-?\d+$/.test(str);
}

export function uidToRef(uid:string):ObjectRef {
	const idParts = uid.split('@');
	const or:Partial<ObjectRef> = { _is_ref: true };
	if (idParts.length === 3) {
		or.type = parseInt(idParts[0]);
		if (idParts[1].length > 0) or.subType = parseInt(idParts[1]);
		if (idParts[2].length > 0) {
			if (isValidInteger(idParts[2])) or.id = parseInt(idParts[2]);
			else or.id = idParts[2];
		}
		return or as ObjectRef;
	} else return null;
}

export function hasType(or:ObjectRef):boolean {
	return or && or.type !== 0;
}

export function hasIdentity(or:ObjectRef):boolean {
	let id = or?.id;
	return hasType(or) && id && id !== "0" && id !== 0;
}

export function objectRef(srcType: number, srcId?:number|string, srcSubType?:number):ObjectRef {
	return {
		_is_ref: true,
		type: srcType, id: srcId, subType: srcSubType
	};
}

export function isObjectRef(o:any):o is ObjectRef {
	return o != null && typeof o === "object" && o?._is_ref === true;
}

export function parseObjectRef(data:string):ObjectRef {
	if (data == null) return null;
	try {
		const parsed = JSON.parse(data);
		if (isObjectRef(parsed)) return parsed;
	} catch (e) {
		// Probably not a valid JSON ... ignored
	}
	return null;
}

/**
 * !!! params should be used only for non-critical things. some browsers aren't able to transfer them
 */
export function setDataTransfer(dt:DataTransfer, params:DragTypeParams, ref:ObjectRef) {
	const content = JSON.stringify(ref);
	dt.setData(formatDragType(DRAG_TYPE, params), content);
	dt.setData(formatDragType(PLAIN_TYPE, params), content);
}


function formatDragType(type:string, params:DragTypeParams):string {
	let b = type;
	for (const [key, value] of Object.entries(params)) {
		b += ";" + key + "=" + value;
	}
	return b;
}

export function getDataTransfer(dt:DataTransfer):ParsedType {
	const types = parseTypes(dt.types);
	let t = findType(types, DRAG_TYPE);
	if (t == null) t = findType(types, PLAIN_TYPE);
	return t;
}

function parseContentType(contentType:string):ParsedType {
	const [type, ...params] = contentType.split(';');
	const parsedParams:DragTypeParams = {};

	params.forEach(param => {
		const [key, value] = param.split('=');
		parsedParams[key.trim()] = value.trim();
	});
	return {
		type,
		full: contentType,
		dragType: (type === DRAG_TYPE),
		params: parsedParams
	};
}

function parseTypes(types: ReadonlyArray<string>):ParsedType[] {
	const pt:ParsedType[] = [];
	for (const type of types) {
		pt.push(parseContentType(type));
	}
	return pt;
}

function findType(types:ParsedType[], type:string):ParsedType|undefined {
	for (const t of types) {
		if (type === t.type) return t;
	}
}



interface NearestElm {
	distance: number;
	element: HTMLElement;
	measure: ElementMeasure;
	index: number;
}

export interface PositionResult {
	offset: number;
	index: number;
}

/**
 * This structure describes a layout of the item list for propper
 * drop cursor calculation.
 *
 * This is example spacing:
 * [<--start-->[item]<--gap-->[item]<--gap-->[item]<--end-->]
 */
export interface DropListSpacing {
	/** Gap between container left or top edge and the first item left or top edge */
	start: number;
	/** Gap between container right or bottom edge and the last item right or bottom edge */
	end: number;
	/** Gap between items */
	gap: number;
}

export const ZERO_SPACING:DropListSpacing = { start:0, end:0, gap: 0 };
export const DROP_MARKER_SIZE = 2;
/** How many pixels should be position shifted to compensate for MARKER_WIDTH*/
const MARKER_SHIFT = DROP_MARKER_SIZE / 2;

/***
 	How to get Y position of mouse inside a container?:
 	m = measure of the container
 	containerTop = m.scrollTop + m.viewTop;	   is top edge of the container
	const containerRelativeMouse =
		e.pageY				// position from top edge of the document
		- containerTop 		// relativelly from container age
		+ container.value.scrollTop;	// container can be scrolled


 */

export function calcVerticalPosition(e: DragEvent, container:HTMLElement, childrenSelector:string, spacing= ZERO_SPACING):PositionResult {
	//const DEBUG:string[] = [];
	const m = getElementMeasure(container);
	const containerTop = m.scrollTop + m.viewTop;
	const localY = e.pageY - containerTop + container.scrollTop;	// y relatively from container
	const children = container.querySelectorAll(childrenSelector);
	//DEBUG.push("Container height=" + m.height + " containerTop=" + containerTop + " localY = " + localY + " children=" + (children.length));

	let nearestAbove:NearestElm = null;
	let nearestBelow:NearestElm = null;
	let idx = 0;
	for (let el of children) {
		const chm = getElementMeasure(el as HTMLElement);
		const height = chm.height;
		//const onPageY = chm.scrollTop + chm.viewTop + height/2;
		let distance = localY - ((el as HTMLElement).offsetTop + height/2);	// Compare mouse position to the center of the element
		//DEBUG.push("Child height=" + height + " offsetTop="+(el as HTMLElement).offsetTop+" middle="+((el as HTMLElement).offsetTop + height/2)+"  distance=" + distance);
		if (distance >= 0) {
			// mouse is below this element
			if (nearestAbove) {
				if (nearestAbove.distance > distance) {
					nearestAbove.distance = distance;
					nearestAbove.element = el as HTMLElement;
					nearestAbove.measure = chm;
					nearestAbove.index = idx;
				}
			} else {
				nearestAbove = {
					distance: distance,
					element: el as HTMLElement,
					measure: chm,
					index: idx
				};
			}
		} else {
			// mouse is above this element
			distance  = distance * -1;
			if (nearestBelow) {
				if (nearestBelow.distance > distance) {
					nearestBelow.distance = distance;
					nearestBelow.element = el as HTMLElement;
					nearestBelow.measure = chm;
					nearestBelow.index = idx;
				}
			} else {
				nearestBelow = {
					distance: distance,
					element: el as HTMLElement,
					measure: chm,
					index: idx
				};
			}
		}
		idx++;
	}

	let pixelOffset:number = 0;
	let index = 0;

	if (nearestAbove) {
		if (nearestBelow) {
			// We have both
			const aboveBottomEdge = nearestAbove.element.offsetTop + nearestAbove.measure.height;
			const belowTopEdge = nearestBelow.element.offsetTop;
			pixelOffset = Math.floor(aboveBottomEdge + (belowTopEdge - aboveBottomEdge) / 2 - MARKER_SHIFT);
			index = nearestBelow.index;
			//console.log("["+aboveBottomEdge+", "+belowTopEdge+"] = "+mouseY.value);
		} else {
			// Only above - it is dragged as the last element
			const aboveBottomEdge = nearestAbove.element.offsetTop + nearestAbove.measure.height;
			pixelOffset = Math.floor(Math.min(aboveBottomEdge + spacing.end/2 - MARKER_SHIFT, aboveBottomEdge + spacing.end - DROP_MARKER_SIZE));
			index = nearestAbove.index + 1;
		}
	} else {
		if (nearestBelow) {
			// Only below - it is dragged as the first element
			const belowTopEdge = nearestBelow.element.offsetTop;
			pixelOffset = Math.floor(Math.max(belowTopEdge - spacing.start/2 - MARKER_SHIFT, belowTopEdge - spacing.start));
			index = nearestBelow.index;
		} else {
			index = 0;
		}
	}
	//DEBUG.push("FINAL offset=" + pixelOffset + " index=" + index);
	//console.log(DEBUG.join('\n'));
	//console.log("localY = " + localY + "   e.pageY = " + e.pageY + "  offset = " + pixelOffset);
	return { offset: pixelOffset, index };
}

export function calcHorizontalPosition(e: DragEvent, container:HTMLElement, childrenSelector:string, spacing = ZERO_SPACING):PositionResult {
	const m = getElementMeasure(container);
	const containerLeft = m.scrollLeft + m.viewLeft;
	const localX = e.pageX - containerLeft + container.scrollLeft;	// x relatively from container
	const children = container.querySelectorAll(childrenSelector);

	let nearestBefore:NearestElm = null;
	let nearestAfter:NearestElm = null;
	let idx = 0;
	for (let el of children) {
		const chm = getElementMeasure(el as HTMLElement);
		const width = chm.width;
		let distance = localX - ((el as HTMLElement).offsetLeft + width/2);	// Compare mouse position to the center of the element
		if (distance >= 0) {
			// mouse is before this element
			if (nearestBefore) {
				if (nearestBefore.distance > distance) {
					nearestBefore.distance = distance;
					nearestBefore.element = el as HTMLElement;
					nearestBefore.measure = chm;
					nearestBefore.index = idx;
				}
			} else {
				nearestBefore = {
					distance: distance,
					element: el as HTMLElement,
					measure: chm,
					index: idx
				};
			}
		} else {
			// mouse is above this element
			distance  = distance * -1;
			if (nearestAfter) {
				if (nearestAfter.distance > distance) {
					nearestAfter.distance = distance;
					nearestAfter.element = el as HTMLElement;
					nearestAfter.measure = chm;
					nearestAfter.index = idx;
				}
			} else {
				nearestAfter = {
					distance: distance,
					element: el as HTMLElement,
					measure: chm,
					index: idx
				};
			}
		}
		idx++;
	}

	let pixelOffset:number = 0;
	let index = 0;

	if (nearestBefore) {
		if (nearestAfter) {
			// We have both
			const beforeRightEdge = nearestBefore.element.offsetLeft + nearestBefore.measure.width;
			const afterLeftEdge = nearestAfter.element.offsetLeft;
			pixelOffset = Math.floor(beforeRightEdge + (afterLeftEdge - beforeRightEdge) / 2 - MARKER_SHIFT);
			index = nearestAfter.index;
		} else {
			// Only above - it is dragged as the last element
			const beforeRightEdge = nearestBefore.element.offsetLeft + nearestBefore.measure.width;
			pixelOffset = Math.floor(Math.min(beforeRightEdge + spacing.end/2 - MARKER_SHIFT, beforeRightEdge + spacing.end - DROP_MARKER_SIZE));
			index = nearestBefore.index + 1;
		}
	} else {
		if (nearestAfter) {
			// Only below - it is dragged as the first element
			const beforeLeftEdge = nearestAfter.element.offsetLeft;
			pixelOffset = Math.floor(Math.max(beforeLeftEdge - spacing.start/2 - MARKER_SHIFT, beforeLeftEdge - spacing.start));
			index = nearestAfter.index;
		} else {
			index = 0;
		}
	}
	return { offset: pixelOffset, index };
}